import {useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Scrollbar } from 'react-scrollbars-custom';
import useBodyClass from '../../common/hooks/useBodyClassName';
import Button from '../../common/Button';
import Search from '../../images/search.svg';
import plus from '../../images/managers/plus.svg';
import { BorderedPageWrapperStyled } from '../reports/tenantList/TenantList.styled';
import { StyledReport } from '../reports/report.styled';
import { StyledArchivePage } from './ArchivePage.styled';
import ArchiveCard from './ArchiveCard';
import {UploadFile} from './UploadFile';
import {hideLoader, showLoader} from "../../store/common/slice";
import {useAccessToken} from "../../common/hooks/useAccessToken";
import ErrorsHandler from "../../common/errors/handleErrors";
import {NotificationManager} from "react-notifications";
import {localeIncludes} from "locale-includes";
import {PageTitle} from "../../common/PageTitle";
import {ModalManager} from "../../ui/ModalManager";
import {isAdminSelector} from "../../store/auth/selectors";
import Switch from "../../common/Switch";


const ArchivePage = ({ match }) => {

    const { buildingID } = match.params;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const client = useAccessToken();
    const ltr = useSelector(state => state.common.ltrEnabled);
    const isAdmin = useSelector(isAdminSelector);
    const downloadLink = useRef();

    const [showAddNewRecord, setShowAddNewRecord] = useState(false);
    const [showDeleted, setShowDeleted] = useState(false);
    const [filter, setFilter] = useState('');
    const [files, setFiles] = useState(/** @type {ArchiveFile[] | null} */ null);

    useBodyClass('report');

    useEffect(async () => {
        if (client == null) return;
        if (files == null) {
            dispatch(showLoader());
            try {
                const resp = await client.managers.archive.getList(buildingID, showDeleted);
                setFiles(resp.files);
            } catch(e) {
                ErrorsHandler.notifyClientError(e);
            } finally {
                dispatch(hideLoader());
            }
        }
    }, [client, files]);

    const filteredFiles = useMemo(() => {
        if (filter === '') return files ?? [];
        return (files ?? []).filter(f => localeIncludes(f.Metadata.displayname, filter, { options: { usage: 'search', sensitivity: 'base' } }));
    }, [files, filter]);

    const fileActions = {
        'download': downloadFile,
        'delete': deleteFile,
        'undelete': undeleteFile,
    };

    async function downloadFile(file) {
        try {
            dispatch(showLoader())
            const resp = await client.managers.archive.generateDownloadLink(buildingID, file.Key);
            // this will allow to open in a new tab
            downloadLink.current.href = resp.link;
            downloadLink.current.click();
        } catch (e) {
            ErrorsHandler.notifyClientError(e);
        } finally {
            dispatch(hideLoader());
        }
    }

    async function deleteFile(file) {
        const confirmed = await ModalManager.confirm(t('archive.delete_confirm.title'), t('archive.delete_confirm.question'));
        if (confirmed) {
            try {
                dispatch(showLoader());
                const result = await client.managers.archive.deleteFile(buildingID, file.File);
                if (showDeleted) {
                    const index = files.findIndex(f => f.Key === file.Key);
                    const filesCopy = [...files];
                    filesCopy[index] = result.file;
                    setFiles(filesCopy);
                } else {
                    const filtered = files.filter(f => f.Key !== file.Key);
                    setFiles(filtered);
                }
            } catch (e) {
                ErrorsHandler.notifyClientError(e);
            } finally {
                dispatch(hideLoader());
            }
        }
    }

    async function undeleteFile(file) {
        try {
            dispatch(showLoader());
            const result = await client.managers.archive.undeleteFile(buildingID, file.File);
            const index = files.findIndex(f => f.Key === file.Key);
            const filesCopy = [...files];
            filesCopy[index] = result.file;
            setFiles(filesCopy);
        } catch (e) {
            ErrorsHandler.notifyClientError(e);
        } finally {
            dispatch(hideLoader());
        }
    }

    function handleUploadDone() {
        NotificationManager.success(t('archive.all_upload_done'));
        setShowAddNewRecord(false);
        setFiles(null); // force files reload
    }

    function handleUploadFailed() {
        setShowAddNewRecord(true);
        setFiles(null);
    }

    function handleUploadCancel() {
        setShowAddNewRecord(false);
    }

    const onAddFileClick = () => setShowAddNewRecord(true);

    function handleFileAction(action, file) {
        const actionFn = fileActions[action];
        if (actionFn == null) {
            throw new Error(`Unknown file action ${action}`);
        }
        actionFn(file);
    }

    function handleShowDeletedChange(show) {
        setShowDeleted(show);
        setFiles(null)
    }

    function ScrollDiv(props) {
        const { elementRef, ...restProps } = props;
        return <div {...restProps} ref={elementRef} />;
    }

    return (
        <BorderedPageWrapperStyled>

            <PageTitle text={t('archive.page_title')}></PageTitle>

            <StyledArchivePage ltr={ltr}>
                {!showAddNewRecord && (
                    <StyledReport className='report' ltr={ltr}>
                        <div className='report-wrap'>
                            <div className='report-top'>                   
                                <div className="search">
                                    <input
                                        type="text"
                                        name="search"
                                        onChange={e => setFilter(e.target.value)}
                                        value={filter}
                                    />
                                    <img src={Search} alt={t('search.search')} />
                                </div>
                                { isAdmin && (
                                    <div className="hidden-switch">
                                        <Switch label={t('archive.show_deleted')} checked={showDeleted} onChange={handleShowDeletedChange} />
                                    </div>
                                ) }
                                <Button onClick={onAddFileClick}>{t('reports.addFile')} <img className='plus' src={plus}/></Button>
                            </div>                  
                        </div>
                    </StyledReport>
                )}

                <div className='main-wrapper'>
                    <Scrollbar
                        style={{ width: '100%', height: 'calc(100vh - 200px)' }}
                        trackYProps={{ renderer: props => <ScrollDiv {...props} className="scroll-track" /> }}
                        thumbYProps={{ renderer: props => <ScrollDiv {...props} className="scroll-thumb" /> }}
                        rtl={false}
                        noScrollX
                    >
                        {showAddNewRecord && (
                            <UploadFile archiveFiles={files ?? []} onDone={handleUploadDone} onCancel={handleUploadCancel} onFail={handleUploadFailed} />
                        )}

                        {!showAddNewRecord && (
                            <div>
                                <div className="table-header">
                                    <div className="titles bllink-text-tiny-bold">
                                        <div>{t('reports.fileName')}</div>
                                        <div>{t('reports.fileType')}</div>
                                        <div>{t('reports.uploadDate')}</div>
                                        <div>{t('reports.uploadedBy')}</div>
                                        <div>{t('reports.size')}</div>
                                    </div>
                                    <div></div>
                                    <div></div>
                                </div>
                                <div className='cards-wrapper'>
                                    <a className="download-link" ref={downloadLink} target="_blank">download file</a>
                                    {filteredFiles.map(f => (<ArchiveCard file={f} key={f.Key} onFileAction={action => handleFileAction(action, f)} />))}
                                </div>
                            </div>
                        )}
                    </Scrollbar>
                </div>
            </StyledArchivePage>

        </BorderedPageWrapperStyled>
    )
}

export default ArchivePage;
